import classNames from 'classnames';

import styles from './SpinButtons.module.css';

import { ButtonBase } from '../../ButtonBase';

export type SpinButtonChangeHandler = (newValue: number) => void;

export interface SpinButtonsProps {
  /** Whether the spin buttons should be disabled. */
  disabled?: boolean;
  /**
   * The label of the associated input field (for accessibility). Used to create
   * an aria-label such as `Decrease/Increase <label> by <step>`.
   *
   * @defaultValue ''
   */
  label?: string;
  /** The minimum value for the decrease button. */
  min?: string | number | undefined;
  /** The maximum value for increase button. */
  max?: string | number | undefined;
  /**
   * The amount to increase/decrease.
   *
   * @defaultValue 1
   */
  step?: string | number | undefined;
  /** The field current value. */
  value?: string | number | undefined;
  /** The callback for when the value changes. */
  onChange?: SpinButtonChangeHandler;
}

/**
 * Create a SpinButtons to increase/decrease the given value when up/down
 * buttons are clicked.
 */
export function SpinButtons(props: SpinButtonsProps) {
  const { step = 1, max, min, label = '' } = props;

  const handleUpClick = () => {
    const value = Number(props.value || 0) + Number(step);
    if (max !== undefined && value > Number(max)) {
      return;
    }
    props.onChange?.(value);
  };
  const handleDownClick = () => {
    const value = Number(props.value || 0) - Number(step);
    if (min !== undefined && value < Number(min)) {
      return;
    }
    props.onChange?.(value);
  };

  return (
    <div
      className={classNames(styles.root, {
        [styles.disabled]: props.disabled,
      })}
    >
      <ButtonBase
        onClick={handleUpClick}
        disabled={props.disabled}
        aria-label={`Increase ${String(label)} by ${String(step)}`}
      >
        <svg width="12" height="8" viewBox="0 0 12 8">
          <path d="M12 8L6 0L0 8H12Z" />
        </svg>
      </ButtonBase>
      <ButtonBase
        onClick={handleDownClick}
        disabled={props.disabled}
        aria-label={`Decrease ${String(label)} by ${String(step)}`}
      >
        <svg width="12" height="8" viewBox="0 0 12 8">
          <path d="M0 0L6 8L12 0L0 0Z" />
        </svg>
      </ButtonBase>
    </div>
  );
}
